import { useTeamsByStore } from "@coworker/app/src/core/hooks/useStoreTeams";
import { getByPath } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { getTypedValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import { useUser } from "@coworker/apprestructured/src/shared/hooks/users/useUser";
import {
  isLoading,
  LOADING,
  Loading,
} from "@coworker/apprestructured/src/task-management/loading";
import { useTranslation } from "@coworker/locales";
import { useMemo } from "react";

type User = string | typeof LOADING | null;
type Team = string | typeof LOADING;

type UseUserAndTeamStringsReturn = {
  user: User;
  team: Team;
  originalTeamString: string | typeof LOADING | null;
};

type UseUserAndTeamStringsArgs = {
  task: Record<string, unknown> | Loading;
  userIdField: string;
  teamIdField?: string;
  teamFromUser?: boolean;
};

export function useUserAndTeamStrings({
  task,
  userIdField,
  teamIdField,
  teamFromUser,
}: UseUserAndTeamStringsArgs): UseUserAndTeamStringsReturn {
  const { t } = useTranslation();

  const storeId = getTypedValue<string>(getByPath(task, "store_id"));
  const userId = getTypedValue<string>(getByPath(task, userIdField));
  const taskTeamId = teamIdField
    ? getTypedValue<string>(getByPath(task, teamIdField))
    : undefined;

  const { data: user } = useUser(isLoading(userId) ? "" : userId);
  const { data: teams } = useTeamsByStore(isLoading(storeId) ? null : storeId);

  const teamId = teamFromUser ? user?.teamId : taskTeamId;

  const userString = useMemo(() => {
    if (!userId || user === null) {
      return null;
    }

    if (user === undefined) {
      return LOADING;
    }

    return user.fullName ?? "";
  }, [task, user?.fullName, userId]);

  const teamString = useMemo(() => {
    if (isLoading(task) || isLoading(storeId) || teams === undefined) {
      return LOADING;
    }

    if (!storeId) {
      return null;
    }

    if (teamFromUser && !user) {
      return LOADING;
    }
    return teams.find(({ id }) => id === teamId)?.name ?? null;
  }, [task, teamId, teams, teamFromUser, t, user]);

  return {
    user: userString,
    team: teamString ?? t("unknownTeamString"),
    originalTeamString: teamString,
  };
}
